import { useCallback } from 'react'
import clsx from 'clsx'
import { useDropzone, FileRejection, Accept } from 'react-dropzone'
import { Upload } from '@/assets/icons'
import { kAcceptedFilesList, MAX_FILE_UPLOAD_SIZE_BYTES } from '@/constants/constants-gcs'
import { UploadDisclaimer } from '@/components/disclaimers/UploadDisclaimer'

export type DropZoneProps = {
  /** Callback when files are accepted */
  onFilesAccepted: (files: File[]) => void
  /** Optional callback when files are rejected */
  onFilesRejected?: (rejections: FileRejection[]) => void
  /** Custom accepted file types (defaults to kAcceptedFilesList) */
  acceptedFileTypes?: Accept
  /** Maximum file size in bytes (defaults to 1GB) */
  maxFileSize?: number
  /** Whether multiple files can be selected (defaults to true) */
  multiple?: boolean
  /** Whether the drop zone is disabled */
  disabled?: boolean
  /** Custom icon to display */
  icon?: React.ReactNode
  /** Custom title text */
  title?: string
  /** Custom subtitle text (file formats) */
  subtitle?: string | React.ReactNode
  /** Additional class names for the container */
  className?: string
  /** Error message to display (parent controls error state) */
  errorMessage?: string
}

export default function DropZone({
  onFilesAccepted,
  onFilesRejected,
  acceptedFileTypes = kAcceptedFilesList,
  maxFileSize = MAX_FILE_UPLOAD_SIZE_BYTES,
  multiple = true,
  disabled = false,
  icon,
  title,
  subtitle,
  errorMessage,
  className,
}: DropZoneProps) {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onFilesAccepted(acceptedFiles)
      }
    },
    [onFilesAccepted]
  )

  const onDropRejected = useCallback(
    (fileRejections: FileRejection[]) => {
      onFilesRejected?.(fileRejections)
    },
    [onFilesRejected]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: acceptedFileTypes,
    maxSize: maxFileSize,
    multiple,
    maxFiles: multiple ? undefined : 1,
    disabled,
  })

  const defaultSubtitle = `Supported formats: ${Object.values(acceptedFileTypes).flat().join(', ')}`

  const containerStyles = clsx(
    'min-h-28 p-4 rounded-lg border-2 transition-all duration-200 w-full flex flex-col items-center justify-center transition-all duration-300 ease-in-out',
    className,
    // Disabled styles
    disabled && 'border-brand-neutral-200 border-[1px] bg-brand-neutral-100 cursor-not-allowed text-brand-neutral-950',
    // Drag active styles
    !disabled && isDragActive && 'border-brand-500 bg-brand-50',
    // Drag inactive styles
    !disabled && !isDragActive && 'border-dashed border-brand-neutral-300 text-brand-neutral-600 hover:border-brand-neutral-400 cursor-pointer'
  )

  return (
    <div data-ds-component="DropZone" className={containerStyles} {...getRootProps()}>
      <input {...getInputProps()} />

      <div className="flex justify-center items-center my-auto w-6 h-6">{icon ?? <Upload className="w-5 h-5" />}</div>

      {title ? (
        <p className="text-brand-neutral-950 text-center font-medium leading-normal">{title}</p>
      ) : (
        <>
          <p className="text-brand-neutral-900 leading-normal">
            Drag & Drop or <span className="text-brand-500 underline">Choose Files</span>
          </p>
        </>
      )}

      {subtitle ? (
        <p className="mt-1 text-sm/6 text-brand-neutral-800 font-normal text-center leading-normal">{subtitle}</p>
      ) : (
        <div className="flex flex-col gap-1">
          <p className="mt-1 text-sm text-brand-neutral-800 whitespace-pre-wrap">{defaultSubtitle}</p>
          <UploadDisclaimer />
        </div>
      )}

      {errorMessage && <p className="mt-2 text-sm text-red-700">{errorMessage}</p>}
    </div>
  )
}
